import Twemoji from "react-twemoji";

import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { TeamNameDisplayData } from "sockapi/scope-specs/team-list-spec";
import { getTeamCollectibleIcons } from "sockapi/scope-specs/team-collectibles-spec";

import {
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import {
  useGlobalScopeState,
  useGlobalScopeStatePosthuntable,
  useScopeStateResolvable,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import { Link } from "components/Link";
import { Spinner } from "components/Loading";
import Tooltip from "components/Tooltip";
import DownloadFailed from "components/DownloadFailed";
import { clsObj } from "client-util/util";
import settings from "settings";
import "./TeamDisplayName.css";

const teamScopeStateToTeamNameDisplayData = (
  teamId: string,
  { displayName }: SockAPIScopeState<ScopeType.TEAM>,
  teamCollectiblesScopeState: SockAPIScopeState<ScopeType.TEAM_COLLECTIBLES>,
  huntEndTime: number | null
): TeamNameDisplayData => {
  const collectibleIcons = getTeamCollectibleIcons(
    teamCollectiblesScopeState,
    huntEndTime
  );
  return {
    teamId,
    displayName,
    collectibleIcons: collectibleIcons === "" ? undefined : collectibleIcons,
  };
};

type TeamDisplayNameRenderProps = {
  invalid?: boolean;
  noLink?: boolean;
  hideAdminLinks?: boolean;
  inline?: boolean;
  hideCollectibles?: boolean;
};

type TeamDisplayNameProps = TeamDisplayNameRenderProps & {
  teamData: TeamNameDisplayData;
};

export const makeTeamPagePath = (displayName: string) =>
  ({
    route: "team",
    params: { teamName: encodeURIComponent(encodeURIComponent(displayName)) },
  }) as const;

export function TeamDisplayNamePreloaded(props: TeamDisplayNameProps) {
  const {
    teamData,
    invalid = false,
    noLink,
    hideAdminLinks: hideAdminLinksOpt = false,
    inline = false,
    hideCollectibles = false,
  } = props;
  const { teamId, displayName, collectibleIcons } = teamData;
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const showAdminLinks = !hideAdminLinksOpt && shouldDisplayAdmin;
  const huntAdminScopeState = useGlobalScopeState(
    ScopeType.HUNT_ADMIN,
    showAdminLinks
  );

  if (showAdminLinks && huntAdminScopeState === null)
    return <Spinner inline={true} />;
  const userId = huntAdminScopeState?.teamAdminData[teamId]?.userId ?? null;

  const twemojiDisplayName = (
    <span className="team-display-name">
      {!hideCollectibles && (collectibleIcons ?? "").length > 0 ? (
        <span className="team-name-collectibles">
          <Twemoji tag="span">{collectibleIcons}</Twemoji>&nbsp;
        </span>
      ) : null}
      <Twemoji tag="span">{displayName}</Twemoji>
    </span>
  );
  const modifiedDisplayName = invalid ? (
    <s>{twemojiDisplayName}</s>
  ) : (
    twemojiDisplayName
  );
  const className = clsObj({
    "team-name-link": true,
    "team-name-link-inline": inline,
    "team-name-invalid": invalid,
  });
  return (
    <>
      {showAdminLinks ? (
        <span className="button-squish-zone team-name-collectibles">
          {
            // If impersonating, we need to stop impersonating
            // first, otherwise the impersonate button doesn't work.
            userId !== null && !settings.isImpersonate ? (
              <Link route="impersonate" params={{ userId }} button={true}>
                <Tooltip text="Impersonate">
                  <Twemoji tag="span">🕵️</Twemoji>
                </Tooltip>
              </Link>
            ) : null
          }
          <Link route="sock_admin_team" params={{ teamId }} button={true}>
            <Tooltip text="Sock Admin">
              <Twemoji tag="span">🧦️</Twemoji>
            </Tooltip>
          </Link>
          &nbsp;
        </span>
      ) : null}
      {noLink ? (
        <span className={className}>{modifiedDisplayName}</span>
      ) : (
        <Link className={className} {...makeTeamPagePath(displayName)}>
          {modifiedDisplayName}
        </Link>
      )}
    </>
  );
}

/** Team display name display for a team we have full access to. */
function AccessibleTeamDisplayName(
  props: TeamDisplayNameRenderProps & { teamId: string }
) {
  const { teamId, ...renderProps } = props;
  const teamScopeState = useTeamScopeState(teamId);
  const teamCollectiblesScopeState = useScopeStateResolvable(
    ScopeType.TEAM_COLLECTIBLES,
    teamId === null ? null : { teamId }
  );

  if (teamScopeState === null || teamCollectiblesScopeState === null)
    return <Spinner inline={true} />;

  return (
    <TeamDisplayNamePreloaded
      teamData={teamScopeStateToTeamNameDisplayData(
        teamId,
        teamScopeState,
        teamCollectiblesScopeState,
        // Show all collectibles for our own team even after hunt ends,
        // for posthunt progression.
        null
      )}
      {...renderProps}
    />
  );
}

/** Team display name display for a team we don't have full access to. */
function InaccessibleTeamDisplayName(
  props: TeamDisplayNameRenderProps & { teamId: string }
) {
  const { teamId, ...renderProps } = props;

  const posthuntTeamListResult = useGlobalScopeStatePosthuntable(
    ScopeType.TEAM_LIST,
    true,
    { hasPosthunt: true }
  );
  const teamList = useGlobalScopeState(ScopeType.TEAM_LIST);

  if (posthuntTeamListResult === null || teamList === null)
    return <Spinner inline />;
  const liveTeamData = teamList[teamId];
  // HACK: The posthunt team always takes priority, but if download
  // failed, fallback to the live team if it exists.
  if (!posthuntTeamListResult.success && liveTeamData === undefined)
    return <DownloadFailed {...posthuntTeamListResult} />;
  const posthuntTeamData = posthuntTeamListResult.success
    ? posthuntTeamListResult.state[teamId]
    : undefined;
  const teamData = posthuntTeamData ?? liveTeamData;

  // If a team is newly created, it may take a little while
  // before it gets reflected in the team list.
  if (teamData === undefined) return <Spinner inline={true} />;

  return <TeamDisplayNamePreloaded teamData={teamData} {...renderProps} />;
}

function TeamDisplayName(
  props: TeamDisplayNameRenderProps & { teamId?: string }
) {
  const { teamId: teamIdOpt, ...renderProps } = props;
  const ownTeamId = useTeamId();
  const teamId = teamIdOpt ?? ownTeamId;

  if (ownTeamId === null || teamId === null) return <Spinner inline={true} />;

  return teamId === ownTeamId ? (
    <AccessibleTeamDisplayName teamId={teamId} {...renderProps} />
  ) : (
    <InaccessibleTeamDisplayName teamId={teamId} {...renderProps} />
  );
}

export default TeamDisplayName;
